import { ReactNode, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import translationKeys from '@translationKeys'
import { Nullable } from '@types'

import InputWithTitleSection from '@components/InputWithTitleSection'
import shortlistApi from '@modules/shortlist/api'
import { Shortlist } from '@modules/shortlist/types'
import { useMutation, useQueryClient } from '@tanstack/react-query'

export interface AddNewShortlistSectionProps {
  shortlists: Shortlist[]
  onCancel: (name: string) => boolean
  onSuccess?: (sh?: Shortlist) => void
  onChange?: (name: string) => void
  isUniqueName?: (name: string) => boolean
  children?: ReactNode
  middleButtonOnClick?: (cb: () => void) => void
  middleButtonText?: string
  submitButtonText?: string
  isSubmitButtonEnabled?: boolean
  subTitle?: string
  title?: Nullable<string>
  selectable?: boolean
  checked?: boolean
  onSelect?: () => void
  hideInput?: boolean
}

const useCreateShortlistInput = ({
  onSuccess,
  isUniqueName,
  shortlists,
}: {
  onSuccess: AddNewShortlistSectionProps['onSuccess']
  shortlists: AddNewShortlistSectionProps['shortlists']
  isUniqueName: AddNewShortlistSectionProps['isUniqueName']
}) => {
  const inputRef = useRef<Nullable<HTMLInputElement>>(null)
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  const { mutate: createShortlist } = useMutation({
    mutationFn: shortlistApi.createShortlist.fn,
    onSuccess: sh => {
      queryClient.invalidateQueries({ queryKey: [shortlistApi.getShortlists.key] })
      inputRef?.current?.focus()
      onSuccess?.(sh)
    },
  })

  const validate = (name: string) => {
    if (!name) {
      return ''
    }

    let isUnique = false

    if (isUniqueName) {
      isUnique = isUniqueName(name)
    } else {
      isUnique = !shortlists.some(sh => sh.name?.toLowerCase() === name.toLowerCase())
    }

    if (!isUnique) {
      return t(translationKeys.CUSTOM_LIST_NAME_UNIQUE_ERROR)
    }

    return ''
  }

  return { inputRef, validate, createShortlist }
}

const AddNewShortlistSection = (props: AddNewShortlistSectionProps) => {
  const {
    shortlists,
    children,
    submitButtonText,
    middleButtonOnClick,
    middleButtonText,
    onSuccess,
    onChange,
    onCancel,
    isUniqueName,
    isSubmitButtonEnabled,
    subTitle,
    title,
    selectable,
    checked,
    onSelect,
    hideInput,
  } = props
  const { t } = useTranslation()
  const { inputRef, validate, createShortlist } = useCreateShortlistInput({ shortlists, onSuccess, isUniqueName })

  return (
    <InputWithTitleSection
      // eslint-disable-next-line
      inputRefToParent={ref => (inputRef.current = ref)}
      onSelect={onSelect}
      hideInput={hideInput}
      checked={checked}
      selectable={selectable}
      subTitle={subTitle}
      title={title || t(translationKeys.ADD_YOUR_CUSTOM_LIST)}
      label={t(translationKeys.NAME_YOUR_CUSTOM_LIST)}
      onChange={onChange}
      onCancel={onCancel}
      validate={validate}
      onSubmit={(name, cb) => {
        if ((!selectable || checked) && name) {
          createShortlist({ name })
        } else {
          onSuccess?.()
        }
        cb()
      }}
      middleButtonText={middleButtonText}
      middleButtonOnClick={middleButtonOnClick}
      submitButtonText={submitButtonText || (t(translationKeys.CUSTOM_LIST_POPUP_ADD_BTN) as string)}
      isSubmitButtonEnabled={isSubmitButtonEnabled}
    >
      {children}
    </InputWithTitleSection>
  )
}

export default AddNewShortlistSection
